/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo } from 'react';
import './AdminUsers.css';
import './StudyTopics.css';
import { apiFetch } from './api';
import MathText from './MathText';
import CourseDropdown from './CourseDropdown';
import { useActiveCourse } from './ActiveCourseContext';

const LEVEL_LABEL = { 'SEVİYE 1': '1. Seviye', 'SEVİYE 2': '2. Seviye', 'SEVİYE 3': '3. Seviye' };
const LEVEL_CLASS = { 'SEVİYE 1': 'seviye1', 'SEVİYE 2': 'seviye2', 'SEVİYE 3': 'seviye3' };

function parseQuestionSections(content) {
  const regex = /===\s*(SEVİYE\s*[123])\s*===/gi;
  const tokens = content.split(regex);
  const sections = [];
  if (tokens[0].trim()) sections.push({ level: null, text: tokens[0].trim() });
  for (let i = 1; i < tokens.length; i += 2) {
    const level = tokens[i].trim().toUpperCase().replace(/\s+/g, ' ');
    const text = (tokens[i + 1] || '').trim();
    sections.push({ level, text });
  }
  return sections;
}

function StudyTopics({ userType }) {
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedCourseId, setSelectedCourseId] = useState('');

  const [topics, setTopics] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [topicsLoaded, setTopicsLoaded] = useState(false);
  const [error, setError] = useState('');
  const [deletingTopicId, setDeletingTopicId] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState('');
  const activeCourseContext = useActiveCourse();
  const isInstructorLike = ['instructor', 'program_baskan', 'rektor', 'rektor_yardimcisi'].includes(userType);

  useEffect(() => {
    fetchCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType]);

  const fetchCourses = async () => {
    try {
      setLoadingCourses(true);
      setError('');
      const endpoint = isInstructorLike ? '/instructor/courses' : '/student/courses';
      const response = await apiFetch(endpoint);
      const data = await response.json();
      if (data.success) {
        setCourses(data.courses || []);
      } else {
        setError(data.message || 'Dersler yüklenemedi');
      }
    } catch {
      setError('Bağlantı hatası');
    } finally {
      setLoadingCourses(false);
    }
  };

  // Instructor-like kullanıcılar için bölüm listesi (benzersiz program_name değerleri)
  const programs = useMemo(() => {
    if (!isInstructorLike) return [];
    const seen = new Set();
    const list = [];
    courses.forEach((c) => {
      const p = c.program_name || '';
      if (p && !seen.has(p)) {
        seen.add(p);
        list.push(p);
      }
    });
    return list.sort((a, b) => a.localeCompare(b, 'tr'));
  }, [courses, isInstructorLike]);

  // Seçili bölüme göre filtrelenmiş dersler
  const filteredCourses = useMemo(() => {
    if (!isInstructorLike) return courses;
    if (!selectedProgram) return [];
    return courses.filter((c) => (c.program_name || '') === selectedProgram);
  }, [courses, selectedProgram, isInstructorLike]);

  const fetchTopics = async (courseId) => {
    try {
      setLoadingTopics(true);
      setError('');
      setTopics([]);
      setTopicsLoaded(false);
      const endpoint = isInstructorLike
        ? `/instructor/study-topics?course_id=${courseId}`
        : `/student/study-topics?course_id=${courseId}`;
      const response = await apiFetch(endpoint);
      const data = await response.json();
      if (data.success) {
        setTopics(data.topics || []);
        setTopicsLoaded(true);
      } else {
        setError(data.message || 'Çalışma konuları yüklenemedi');
      }
    } catch {
      setError('Bağlantı hatası');
    } finally {
      setLoadingTopics(false);
    }
  };

  const handleDeleteTopic = async (topic) => {
    const createdAt = topic.created_at ? new Date(topic.created_at) : null;
    const createdLabel = createdAt && !Number.isNaN(createdAt.getTime())
      ? createdAt.toLocaleString('tr-TR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : 'tarihi belirtilmeyen';
    const confirmed = window.confirm(
      `${createdLabel} tarihinde oluşturulan bu çalışma konusu aktarımını silmek istediğinize emin misiniz?\n\nBu işlem yalnızca seçili aktarımı siler ve geri alınamaz.`
    );
    if (!confirmed) return;

    try {
      setDeletingTopicId(topic.id);
      setDeleteError('');
      setDeleteSuccess('');
      const response = await apiFetch(`/instructor/study-topics/${topic.id}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Çalışma konusu silinemedi');
      }
      setTopics((currentTopics) => currentTopics.filter((item) => item.id !== topic.id));
      setDeleteSuccess(`${createdLabel} tarihli çalışma konusu aktarımı silindi.`);
    } catch (deleteRequestError) {
      setDeleteError(deleteRequestError.message || 'Silme işlemi sırasında bağlantı hatası oluştu.');
    } finally {
      setDeletingTopicId(null);
    }
  };

  const handleProgramChange = (program) => {
    setSelectedProgram(program);
    setSelectedCourseId('');
    setTopics([]);
    setTopicsLoaded(false);
    setError('');
  };

  const handleCourseChange = (courseId) => {
    setSelectedCourseId(courseId);
    setTopics([]);
    setTopicsLoaded(false);
    setError('');
    if (courseId) fetchTopics(courseId);
    if (activeCourseContext) activeCourseContext.setActiveCourseId(courseId);
  };

  useEffect(() => {
    if (!activeCourseContext) return;
    const nextCourseId = activeCourseContext.activeCourseId;
    if (String(selectedCourseId) === String(nextCourseId)) return;
    setSelectedCourseId(nextCourseId);
    setTopics([]);
    setTopicsLoaded(false);
    setError('');
    if (nextCourseId) fetchTopics(nextCourseId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCourseContext?.activeCourseId, userType]);

  // Tarihe göre grupla
  const groupedByDate = useMemo(() => {
    const groups = {};
    topics.forEach((t) => {
      const date = t.created_at ? new Date(t.created_at) : null;
      const dateKey = date
        ? date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : 'Tarihsiz';
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(t);
    });
    const sorted = Object.entries(groups).sort(([a], [b]) => {
      if (a === 'Tarihsiz') return 1;
      if (b === 'Tarihsiz') return -1;
      const [da, ma, ya] = a.split('/').map(Number);
      const [db, mb, yb] = b.split('/').map(Number);
      return new Date(yb, mb - 1, db) - new Date(ya, ma - 1, da);
    });
    return sorted;
  }, [topics]);

  const selectedCourse = courses.find((c) => String(c.course_id) === String(selectedCourseId));

  return (
    <div className="au-page">
      <div className="au-toolbar">
        <h2 className="au-title">Çalışma Konuları</h2>
      </div>

      {/* Aktif ders çubuğu olmayan ekranlarda ders seçimi burada yapılır. */}
      {!activeCourseContext && (
        <div className="st-selector-panel">
          <CourseDropdown
            courses={courses}
            selectedCourseId={selectedCourseId}
            onChange={handleCourseChange}
            loading={loadingCourses}
          />
        </div>
      )}

      {/* Hata */}
      {error && (
        <div className="st-error">
          <p>{error}</p>
          <button className="st-retry-btn" onClick={() => selectedCourseId && fetchTopics(selectedCourseId)}>
            Tekrar Dene
          </button>
        </div>
      )}

      {/* Ders seçilmedi */}
      {!error && !selectedCourseId && (
        <div className="st-empty">
          <div className="st-empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
          </div>
          <p className="st-empty-text">
            {activeCourseContext
              ? 'Çalışma konularını görüntülemek için üstteki Aktif Ders alanından bir ders seçin.'
              : 'Çalışma konularını görüntülemek için bir ders seçin.'}
          </p>
        </div>
      )}

      {/* Yükleniyor */}
      {!error && selectedCourseId && loadingTopics && (
        <div className="st-loading">
          <div className="st-spinner" />
          <p>Yükleniyor...</p>
        </div>
      )}

      {/* Konular */}
      {!error && selectedCourseId && !loadingTopics && topicsLoaded && (
        <>
          {selectedCourse && (
            <div className="st-course-title">
              <span className="st-course-title-badge">
                {selectedCourse.course_code || selectedCourse.course_name}
              </span>
              {selectedCourse.course_code && (
                <span className="st-course-title-name">{selectedCourse.course_name}</span>
              )}
            </div>
          )}

          {deleteError && (
            <div className="st-action-message st-action-message-error" role="alert">
              {deleteError}
            </div>
          )}

          {deleteSuccess && (
            <div className="st-action-message st-action-message-success" role="status">
              {deleteSuccess}
            </div>
          )}

          {groupedByDate.length === 0 ? (
            <div className="st-empty">
              <div className="st-empty-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                </svg>
              </div>
              <p className="st-empty-text">Bu derse ait henüz çalışma konusu eklenmemiş.</p>
            </div>
          ) : (
            <div className="st-groups">
              {groupedByDate.map(([dateLabel, dateTopics]) => (
                <div key={dateLabel} className="st-date-group">
                  <div className="st-date-header">
                    <span className="st-date-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </span>
                    <span className="st-date-label">{dateLabel} tarihli çalışma konuları</span>
                    <span className="st-date-count">{dateTopics.length} konu</span>
                  </div>
                  <div className="st-topic-list">
                    {dateTopics.map((topic) => (
                      <div key={topic.id} className="st-topic-card">
                        <div className="st-topic-header">
                          {topic.hafta_no != null && (
                            <span className="st-week-badge">
                              {(Array.isArray(topic.hafta_no) ? topic.hafta_no.join(', ') : topic.hafta_no)}. Hafta
                            </span>
                          )}
                          {topic.created_at && (
                            <span className="st-created-time">
                              Oluşturma: {new Date(topic.created_at).toLocaleTimeString('tr-TR', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          )}
                          {isInstructorLike && (
                            <button
                              type="button"
                              className="st-delete-btn"
                              onClick={() => handleDeleteTopic(topic)}
                              disabled={deletingTopicId === topic.id}
                              aria-label={`${topic.subject || 'Çalışma konusu'} aktarımını sil`}
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6l-1 14H6L5 6" />
                                <path d="M10 11v6M14 11v6" />
                                <path d="M9 6V4h6v2" />
                              </svg>
                              {deletingTopicId === topic.id ? 'Siliniyor…' : 'Oluşturmayı Sil'}
                            </button>
                          )}
                        </div>
                        <div className="st-topic-subject">{topic.subject}</div>
                        {topic.questions_content && (() => {
                          const sections = parseQuestionSections(topic.questions_content);
                          return (
                            <div className="st-topic-content-wrapper">
                              {sections.map((sec, idx) =>
                                sec.level ? (
                                  <div key={idx} className={`st-section st-section-${LEVEL_CLASS[sec.level] || sec.level.toLowerCase()}`}>
                                    <div className="st-section-header">{LEVEL_LABEL[sec.level] || sec.level}</div>
                                    <div className="st-section-body">
                                      {sec.text.split('\n').map((line, li) => (
                                        <div key={li}><MathText text={line} /></div>
                                      ))}
                                    </div>
                                  </div>
                                ) : (
                                  <div key={idx} className="st-topic-content">
                                    {sec.text.split('\n').map((line, li) => (
                                      <div key={li}><MathText text={line} /></div>
                                    ))}
                                  </div>
                                )
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default StudyTopics;
